import type { BriefingStructured } from "@connect/shared";

interface Props {
  data: BriefingStructured;
}

/**
 * Editorial renderer for structured briefings. Five typed sections —
 * The Read, Signals That Matter, Opportunities, Watchlist, Reflection —
 * each rendered as its own component. Empty/omitted sections are simply
 * skipped; we never show placeholder copy.
 *
 * Typography is the primary structure. No icons, no chips, no spark
 * elements. Spacing carries the hierarchy.
 */
export function TypedBriefing({ data }: Props) {
  const hasOpps = !!data.opportunities && data.opportunities.length > 0;
  const hasWatch = !!data.watchlist && data.watchlist.length > 0;

  return (
    <article className="space-y-10">
      <TheRead text={data.read} />
      {data.signals.length > 0 && <Signals signals={data.signals} />}
      {hasOpps && <Opportunities opps={data.opportunities!} />}
      {hasWatch && <Watchlist items={data.watchlist!} />}
      {data.reflection && <Reflection text={data.reflection} />}
    </article>
  );
}

function TheRead({ text }: { text: string }) {
  return (
    <section>
      <p className="label-eyebrow text-gold-glow">The Read</p>
      <p className="mt-3 text-lg text-fog leading-relaxed font-display">{text}</p>
    </section>
  );
}

function Signals({
  signals,
}: {
  signals: NonNullable<BriefingStructured["signals"]>;
}) {
  return (
    <section>
      <p className="label-eyebrow">Signals That Matter</p>
      <ul className="mt-4 space-y-5">
        {signals.map((s, i) => (
          <li key={i} className="border-l-2 border-veil/40 pl-4">
            <p className="text-fog leading-relaxed">{s.headline}</p>
            {s.context && (
              <p className="mt-1.5 text-sm text-whisper leading-relaxed">{s.context}</p>
            )}
            {s.source_name && (
              <p className="mt-1.5 text-[10px] uppercase tracking-[0.18em] text-muted">
                via {s.source_name}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Opportunities({
  opps,
}: {
  opps: NonNullable<BriefingStructured["opportunities"]>;
}) {
  return (
    <section>
      <p className="label-eyebrow">Opportunities</p>
      <ul className="mt-4 space-y-4">
        {opps.map((o, i) => (
          <li key={i}>
            <p className="text-fog leading-relaxed">{o.title}</p>
            {o.detail && (
              <p className="mt-1.5 text-sm text-whisper leading-relaxed">{o.detail}</p>
            )}
            {o.deadline && (
              <p className="mt-1.5 text-[10px] uppercase tracking-[0.18em] text-gold-glow/80">
                by {o.deadline}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Watchlist({ items }: { items: string[] }) {
  return (
    <section>
      <p className="label-eyebrow">Watchlist</p>
      <ul className="mt-4 space-y-3">
        {items.map((w, i) => (
          <li
            key={i}
            className="text-fog/90 leading-relaxed pl-5 -indent-5 before:content-['·'] before:text-gold-glow before:mr-2 before:inline-block before:w-2"
          >
            {w}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Reflection({ text }: { text: string }) {
  return (
    <section className="border-t border-veil/40 pt-7">
      <p className="label-eyebrow">Sit with this</p>
      <p className="mt-3 italic text-fog/85 leading-relaxed">{text}</p>
    </section>
  );
}
